import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import classnames from 'classnames'
import isEqual from 'lodash/isEqual'
import LKIcon from 'loankeeper-common/components/LKIcon'
import BaseWindow from 'loankeeper-common/components/Window'

// @ts-ignore
import { actions as LKModalActions, selectors as modalSelectors } from 'shared/LKModal'
// @ts-ignore
import { DocumentTitle } from 'shared/Helpers'
import { toggleFullscreen } from '../store/persist/tabs.actions'
import KeyHandler from './shared/KeyHandler'
import LKModal from './shared/LKModal'
import { useOpenedPages } from './OpenedPages'
import { getEnabledFullscreenTabs } from 'src/store/config'
import { includes } from 'lodash'

const PROMPT_CONFIRM_MODAL_NAME = 'PROMPT_CONFIRM_MODAL_NAME'

const StyledWindow = styled(BaseWindow)`
  &.lk-window.window--fullscreen {
    width: calc(100% - 40px);
    max-width: initial;
    margin-left: 35px;
  }

  .fullscreen-button {
    width: 30px;
    height: 30px;
    font-size: 17px;
    background: transparent;
    color: #fff;
    border: 0;
    outline: 0;

    .icon {
      margin: 0;
    }
  }
`

const FullscreenButton = ({ isFullscreen, toggleFullscreen }) => {
  const icon = isFullscreen ? 'compress' : 'expand'

  return (
    <button type="button" className="fullscreen-button" onClick={() => toggleFullscreen()}>
      <LKIcon semanticIcon={icon} />
    </button>
  )
}

function usePrompt() {
  const dispatch = useDispatch()

  const isOpen = useSelector((state) => modalSelectors.isOpen(state, PROMPT_CONFIRM_MODAL_NAME))

  const openPrompt = React.useCallback(() => {
    dispatch(LKModalActions.openConfirmation(PROMPT_CONFIRM_MODAL_NAME))
  }, [dispatch])

  return [isOpen, openPrompt]
}

function useFullscreenConfig(id) {
  const dispatch = useDispatch()

  const enabledFullscreenTabs = useSelector(getEnabledFullscreenTabs)

  const tabConfig = useSelector((state: any) => state.persisted.tabs[id] || {}, isEqual)

  const handleToggleFullscreen = () => {
    dispatch(toggleFullscreen({ id }))
  }

  return {
    allowFullscreen: includes(enabledFullscreenTabs, id),
    fullscreen: Boolean(tabConfig.fullscreen),
    toggle: handleToggleFullscreen,
  }
}

interface DetailsWindowParams {
  className: string
  id: string | number
  uri: string
  headerText: string
  icon: string
  hint: string
  footer: any
  search: any
  onClose: Function
  confirmClose: boolean
  children: any
}

const DetailsWindow = ({
  children,
  className,
  confirmClose,
  footer,
  headerText,
  hint,
  icon,
  id,
  onClose,
  search,
  uri,
}: Partial<DetailsWindowParams>) => {
  const [isConfirmOpen, openConfirm] = usePrompt()

  const closeOnEscape = useSelector((state) => modalSelectors.getTotalOpenedModals(state) === 0)

  const openedPages = useOpenedPages()

  const { allowFullscreen, fullscreen, toggle: handleToggleFullscreen } = useFullscreenConfig(id)

  function handleClose() {
    if (confirmClose && !isConfirmOpen) {
      openConfirm()
      return
    }

    if (typeof onClose === 'function') {
      onClose()
    }

    openedPages.onRemoveWindow({
      id: uri,
    })
  }

  React.useEffect(() => {
    openedPages.onAddWindow({
      id: uri,
      name: headerText,
      hint,
      icon,
      search,
    })
  }, [uri]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <StyledWindow
      className={classnames(className, {
        'window--fullscreen': fullscreen,
      })}
      icon={icon}
      headerText={headerText}
      headerActions={
        <>
          {allowFullscreen && <FullscreenButton isFullscreen={fullscreen} toggleFullscreen={handleToggleFullscreen} />}
        </>
      }
      hint={hint}
      footer={footer}
      size="large"
      onClose={handleClose}
      allowFullscreen={allowFullscreen}
    >
      <>
        <DocumentTitle title={headerText} />
        {children}
        {/** @ts-ignore */}
        {confirmClose && <LKModal.Confirmation name={PROMPT_CONFIRM_MODAL_NAME} onConfirm={handleClose} />}
        {/** @ts-ignore */}
        {closeOnEscape && <KeyHandler targetKey="27" onKey={handleClose} />}
      </>
    </StyledWindow>
  )
}

export default DetailsWindow
